import React from 'react';
import useBackendApi from '../../hooks/useBackendApi';
import useLocalSuccessErrMsg from '../../hooks/useLocalSuccessErrMsg';
import useStateWithInputEventHandler from '../../hooks/useStateWithInputEventHandler';
import useSteps from '../../hooks/useSteps';
import FormWizard from '../FormWizard/FormWizard';
import SwipeTransition from '../SwipeTransition/SwipeTransition';
import WizardFinishStep from '../WizardFinishStep/WizardFinishStep';
import DateInput from './components/DateInput/DateInput';
import RescheduleOptions from './components/RescheduleOptions/RescheduleOptions';
import TimeConstraintInput from './components/TimeConstraintInput/TimeConstraintInput';
import TimeInput from './components/TimeInput/TimeInput';

interface IChangeDateTimeWizardProps {
  offerId?: string;
  open?: boolean;
  rescheduleInProgress?: boolean;
  rescheduleOffered?: boolean;
  drawerView?: boolean;
  className?: string;
  urlToPostDataTo?: string;
  method?: 'POST' | 'PATCH';
  responseField?: string;
  onClose?: (e?: any) => void;
  onFinish?: (e?: any, action?: string) => void;
}

const ChangeDateTimeWizard: React.FC<React.PropsWithChildren<React.PropsWithChildren<IChangeDateTimeWizardProps>>> = ({
  offerId,
  open,
  onClose,
  onFinish,
  rescheduleInProgress,
  rescheduleOffered,
  drawerView,
  responseField = 'updatedAppointment',
  urlToPostDataTo = `/v1/appointments/customer/actions/request-timechange`,
  method = 'POST',
}) => {
  const { post, patch } = useBackendApi();

  const {
    step,
    steps,
    setSteps,
    setStep,
    isLastStep,
    isFirstStep,
    index,
    next,
    prev,
  } = useSteps(['dateOrTime', 'date', 'timeConstraint', 'time', 'finish']);

  const [input, handleChange, setInput] = useStateWithInputEventHandler({
    changeDate: false,
    changeTime: false,
    date: '',
    timeConstraint: '',
    time: '',
    reason: '',
  });
  const { changeDate, changeTime, date, timeConstraint, time, reason } = input;

  const {
    loading,
    setLoading,
    err,
    setErr,
    clearErr,
    success,
    setSuccess,
    clearSuccess,
  } = useLocalSuccessErrMsg();

  React.useEffect(() => {
    if (step === 'dateOrTime' && (changeDate || changeTime)) {
      (!changeDate && setStep('timeConstraint')) || setStep('date');
    }
  }, [steps]);

  React.useEffect(() => {
    if (isLastStep) {
      success && clearSuccess();
      err && clearErr();
      submitDateTimeChangeRequest();
    }
  }, [isLastStep]);

  function handleBack() {
    if (isFirstStep) {
      return onClose?.();
    }
    prev();
  }

  async function handleNext() {
    if (isLastStep) {
      return onClose?.();
    }
    if (step === 'dateOrTime') {
      let newSteps = ['dateOrTime'];
      changeDate && newSteps.push('date');
      changeTime && newSteps.push('timeConstraint', 'time');
      newSteps.push('finish');
      return setSteps(newSteps);
    }
    next();
  }

  function handleClear() {
    let value: any = {};
    switch (step) {
      case 'time':
        let field = `${timeConstraint}Time`;
        value[field] = '';
        break;
      case 'dateOrTime':
        value.changeDate = '';
        value.changeTime = '';
        break;
      default:
        value[step] = '';
    }
    setInput({ ...input, ...value });
  }

  async function submitDateTimeChangeRequest() {
    let data: any = { appointmentId: offerId };
    if (changeDate) {
      data.eventDate = date;
    }
    if (changeTime) {
      const field = `${timeConstraint}Time`;
      data[field] = time;
      data.reason = reason;
    }
    const url = urlToPostDataTo;
    setLoading(true);
    const ajaxFn =
      (method === 'POST' && post) ||
      (method === 'PATCH' && patch) ||
      (() => null);
    const res = await ajaxFn(url, data, {
      loader: false,
      onError: (err) => {
        setErr(err.message);
        setLoading(false);
      },
    });
    setSuccess('Date/Time change requested');
    const result = (responseField && res.data?.[responseField]) || res.data;
    onFinish?.(result);
    setLoading(false);
  }

  const isFormValid = React.useMemo(() => {
    switch (step) {
      case 'dateOrTime':
        return !!(changeDate || changeTime);
      case 'date':
        return !!date;
      case 'timeConstraint':
        return !!timeConstraint;
      case 'time':
        return timeConstraint && time && reason;
      default:
        return true;
    }
  }, [input, step]);

  const stepHeader = React.useMemo(() => {
    const stepsHeadingsMapping = {
      dateOrTime: {
        subTitle: 'Change Date/Time',
        title: 'What would you like to change?',
      },
      date: {
        subTitle: 'Date',
        title: 'When is your event?',
      },
      timeConstraint: {
        subTitle: 'Change time',
        title: 'Time',
      },
      startTime: {
        subTitle: 'Start Time',
        title: 'What time would you like to start after?',
      },
      finishTime: {
        subTitle: 'Finish Time',
        title: 'What time would you like to be ready by',
      },
      finish: {
        subTitle: 'Submit Request',
        title: 'Finish',
      },
    };
    let field = step;
    if (step === 'time') {
      field = `${timeConstraint}Time`;
    }
    return stepsHeadingsMapping[field];
  }, [step, timeConstraint]);

  return (
    <FormWizard
      open={open}
      onClose={onClose}
      onBack={handleBack}
      hideBackBtn={isLastStep}
      title={stepHeader?.title}
      subTitle={stepHeader?.subTitle}
      onNext={handleNext}
      nextBtnDisabled={!isFormValid}
      drawerView={drawerView}
      nextBtnLabel={(isLastStep && 'Finish') || 'Next'}
      onClear={handleClear}
      loading={loading}
      hideClearBtn={isLastStep}
    >
      {!rescheduleInProgress && !rescheduleOffered && (
        <SwipeTransition activeStepIndex={index} duration="500">
          <RescheduleOptions
            changeDate={changeDate}
            changeTime={changeTime}
            onChange={handleChange}
          />

          {changeDate && <DateInput value={date} onChange={handleChange} />}
          {changeTime && (
            <TimeConstraintInput
              value={timeConstraint}
              onChange={handleChange}
            />
          )}
          {changeTime && (
            <TimeInput
              timeConstraint={timeConstraint}
              time={time}
              reason={reason}
              onChange={handleChange}
            />
          )}

          <WizardFinishStep
            loading={loading}
            success={success}
            err={err}
            extension={
              !!success && (
                <div className="mt-8 lg:text-lg">
                  We will email and text you if the stylist is able to
                  accomodate the new date/time request
                </div>
              )
            }
          />
        </SwipeTransition>
      )}
    </FormWizard>
  );
};
export default ChangeDateTimeWizard;
